import { DeepPartial } from '@apollo/client/utilities'
import { BankingValueObjectsCardTypeEnum } from 'generated/resources/datatable.graphql'
import { Approvable } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { ApprovableContentComponent } from 'modules/inbox_tasks/tasks/approval/types'

import ATSRequisitionTaskContent from './AtsRequisition'
import AttendanceEditTimesheetRequestTaskContent from './AttendanceEditTimesheetRequest'
import AttendanceOvertimeRequestTaskContent from './AttendanceOvertimeRequest'
import BankingCardTaskContent from './BankingCard'
import ExpensableExpenseTaskContent from './ExpensesExpensable'
import ProcurementPurchaseRequestTaskContent from './ProcurementPurchaseRequest'
import ShiftManagementSwapShiftTaskContent from './ShiftManagementSwapShift'
import TimeoffLeaveTaskContent from './TimeoffLeave'
import TrainingsTrainingRequestTaskContent from './TrainingsTrainingRequest'

type ApprovableTypename = NonNullable<Approvable['__typename']>

export const getResourceTextFromResourceType = (resourceType: string): string => {
  switch (resourceType) {
    case 'Ats::Requisition':
      return i18n.t('tasks.approvals.ats_requisition.resource')
    case 'Attendance::EditTimesheetRequest':
      return i18n.t('tasks.approvals.attendance_edit_timesheet_request.resource')
    case 'Attendance::OvertimeRequest':
      return i18n.t('tasks.approvals.attendance_overtime_request.resource')
    case 'Contracts::ContractVersionFake':
      return i18n.t('tasks.approvals.contracts_contract_version.resource')
    case 'Contracts::ContractVersion':
      return i18n.t('tasks.approvals.contracts_contract_version.resource')
    case 'Expenses::Expensable':
      return i18n.t('tasks.approvals.expenses_expensable.resource')
    case 'IntegrationsJoin::Order':
      return i18n.t('tasks.approvals.integrations_join_order.resource')
    case 'Procurement::PurchaseRequest':
      return i18n.t('tasks.approvals.procurement_purchase_request.resource')
    case 'ShiftManagement::SwapShift':
      return i18n.t('tasks.approvals.shift_management_swap_shift.resource')
    case 'Timeoff::Leave':
      return i18n.t('tasks.approvals.timeoff_leave.resource')
    case 'Trainings::TrainingRequest':
      return i18n.t('tasks.approvals.trainings_training_request.resource')
    case 'Banking::Card':
      return i18n.t('tasks.approvals.banking_card.resource.default')
    default:
      return ''
  }
}

type ResourceTitles = Record<
  ApprovableTypename,
  | ((approvable?: DeepPartial<Approvable>) => string)
  | ((
      approvable?: DeepPartial<Approvable>
    ) => (bankingCardType: BankingValueObjectsCardTypeEnum) => string)
>

export const RESOURCE_TITLES: ResourceTitles = {
  AtsRequisition: () => i18n.t('tasks.approvals.ats_requisition.resource'),
  AttendanceEditTimesheetRequest: () =>
    i18n.t('tasks.approvals.attendance_edit_timesheet_request.resource'),
  AttendanceOvertimeRequest: () => i18n.t('tasks.approvals.attendance_overtime_request.resource'),
  BankingCard: () => (bankingCardType: BankingValueObjectsCardTypeEnum) => {
    switch (bankingCardType) {
      case 'physical':
        return i18n.t('tasks.approvals.banking_card.resource.physical')
      case 'virtual':
        return i18n.t('tasks.approvals.banking_card.resource.virtual')
      case 'single_use':
        return i18n.t('tasks.approvals.banking_card.resource.single_use')
      default:
        return ''
    }
  },
  ContractsContractVersion: () => i18n.t('tasks.approvals.contracts_contract_version.resource'),
  ContractsContractVersionRequest: () =>
    i18n.t('tasks.approvals.contracts_contract_version.resource'),
  ExpensesExpensable: (approvable) =>
    approvable?.__typename === 'ExpensesExpensable' && approvable.expensableGroup
      ? i18n.t('tasks.approvals.expenses_group.resource')
      : i18n.t('tasks.approvals.expenses_expensable.resource'),
  ProcurementPurchaseRequest: () => i18n.t('tasks.approvals.procurement_purchase_request.resource'),
  ShiftManagementSwapShift: () => i18n.t('tasks.approvals.shift_management_swap_shift.resource'),
  TimeoffLeave: () => i18n.t('tasks.approvals.timeoff_leave.resource'),
  TrainingsTrainingRequest: () => i18n.t('tasks.approvals.trainings_training_request.resource'),
  IntegrationsJoinOrder: () => i18n.t('tasks.approvals.integrations_join_order.resource'),
}

export const RESOURCE_COMPONENTS: Partial<Record<ApprovableTypename, ApprovableContentComponent>> =
  {
    AtsRequisition: ATSRequisitionTaskContent,
    AttendanceEditTimesheetRequest: AttendanceEditTimesheetRequestTaskContent,
    AttendanceOvertimeRequest: AttendanceOvertimeRequestTaskContent,
    BankingCard: BankingCardTaskContent,
    ExpensesExpensable: ExpensableExpenseTaskContent,
    ProcurementPurchaseRequest: ProcurementPurchaseRequestTaskContent,
    ShiftManagementSwapShift: ShiftManagementSwapShiftTaskContent,
    TimeoffLeave: TimeoffLeaveTaskContent,
    TrainingsTrainingRequest: TrainingsTrainingRequestTaskContent,
  }
